import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Fade,
  Grow,
  Chip,
} from '@mui/material';
import { 
  Chat as ChatIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Message as MessageIcon,
  Speed as SpeedIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Insights as InsightsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { createTestData } from '../utils/createTestData';
import DashboardLayout from './layout/DashboardLayout';
import StatsCard from './cards/StatsCard';

// Componentes
import MonthlyChart from './statistics/MonthlyChart';
import DailyChart from './statistics/DailyChart';
import MonthlyChatsChart from './statistics/MonthlyChatsChart';
import DailyChatsChart from './statistics/DailyChatsChart';
import ContactsList from './statistics/ContactsList';
import RealtimeChats from './statistics/RealtimeChats';

const Dashboard = () => {
  const { logout, currentUser } = useAuth();
  const [stats, setStats] = useState({
    totalMessages: 0,
    totalContacts: 0,
    messagesThisMonth: 0,
    messagesToday: 0,
    totalChats: 0,
    chatsThisMonth: 0,
    chatsToday: 0
  });
  const [chats, setChats] = useState([]);
  const [loadingTestData, setLoadingTestData] = useState(false);

  useEffect(() => {
    // Escuchar cambios en tiempo real de la colección chats
    const chatsQuery = query(
      collection(db, 'chats')
    );

    const unsubscribe = onSnapshot(chatsQuery, (snapshot) => {
      const chatsData = [];
      let totalMessages = 0;
      const today = new Date();
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      
      let messagesThisMonth = 0;
      let messagesToday = 0;
      let chatsThisMonth = 0;
      let chatsToday = 0;

      snapshot.forEach((doc) => {
        const chatData = { id: doc.id, ...doc.data() };
        chatsData.push(chatData);
        
        // Contar chats creados este mes y hoy
        if (chatData.createdAt) {
          const chatCreatedDate = chatData.createdAt?.toDate ? chatData.createdAt.toDate() : new Date(chatData.createdAt);
          if (chatCreatedDate >= startOfMonth) {
            chatsThisMonth++;
          }
          if (chatCreatedDate >= startOfDay) {
            chatsToday++;
          }
        }
        
        if (chatData.messages) {
          totalMessages += chatData.messages.length;
          
          // Contar mensajes de este mes
          chatData.messages.forEach(message => {
            const messageDate = message.timestamp?.toDate ? message.timestamp.toDate() : new Date(message.timestamp);
            if (messageDate >= startOfMonth) {
              messagesThisMonth++;
            }
            if (messageDate >= startOfDay) {
              messagesToday++;
            }
          });
        }
      });

      setChats(chatsData);
      setStats({
        totalMessages,
        totalContacts: chatsData.length,
        messagesThisMonth,
        messagesToday,
        totalChats: chatsData.length,
        chatsThisMonth,
        chatsToday
      });
    }, (error) => {
      console.error('Error connecting to Firestore:', error);
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const handleCreateTestData = async () => {
    setLoadingTestData(true);
    try {
      await createTestData();
      alert('¡Datos de prueba creados exitosamente! El dashboard se actualizará automáticamente.');
    } catch (error) {
      console.error('Error creando datos de prueba:', error);
      alert('Error creando datos de prueba. Revisa la consola para más detalles.');
    } finally {
      setLoadingTestData(false);
    }
  };

  return (
    <DashboardLayout stats={stats} chats={chats}>
      {/* Mensaje cuando no hay datos */}
      {chats.length === 0 && (
        <Fade in timeout={800}>
          <Paper 
            sx={{ 
              p: 4, 
              mb: 4, 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 3,
            }}
          >
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              🚀 ¡Bienvenido al Dashboard de Miranda Bot!
            </Typography>
            <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
              Tu centro de control inteligente
            </Typography>
            <Box display="flex" justifyContent="center" gap={4} flexWrap="wrap">
              <Box textAlign="center">
                <AssessmentIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="body1">
                  Usa el bot para generar datos reales
                </Typography>
              </Box>
              <Box textAlign="center">
                <InsightsIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="body1">
                  O crea datos de prueba para explorar
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Fade>
      )}
      
      {/* Tarjetas de estadísticas principales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Grow in timeout={600}>
            <Box>
              <StatsCard
                title="Total Mensajes"
                value={stats.totalMessages}
                icon={<MessageIcon />}
                color="primary"
                trend="up"
                trendValue="+12%"
                subtitle={`${stats.messagesToday} hoy`}
              />
            </Box>
          </Grow>
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <Grow in timeout={800}>
            <Box>
              <StatsCard
                title="Total Chats"
                value={stats.totalChats}
                icon={<ChatIcon />}
                color="secondary"
                trend="up"
                trendValue="+8%"
                subtitle={`${stats.chatsToday} nuevos hoy`}
              />
            </Box>
          </Grow>
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <Grow in timeout={1000}>
            <Box>
              <StatsCard
                title="Este Mes"
                value={stats.messagesThisMonth}
                icon={<TrendingUpIcon />}
                color="success"
                subtitle="Mensajes del mes"
                progress={75}
              />
            </Box>
          </Grow>
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <Grow in timeout={1200}>
            <Box>
              <StatsCard
                title="Promedio"
                value={stats.totalChats > 0 ? Math.round(stats.totalMessages / stats.totalChats) : 0}
                icon={<SpeedIcon />}
                color="info"
                subtitle="Mensajes por chat"
                chip="Eficiencia"
              />
            </Box>
          </Grow>
        </Grid>
      </Grid>

      {/* Gráficos en una sola fila horizontal */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Fade in timeout={1000}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                height: 400,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
                },
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      p: 0.8,
                      borderRadius: 1.5,
                      backgroundColor: 'primary.light',
                      mr: 1.5,
                    }}
                  >
                    <TimelineIcon sx={{ color: 'primary.contrastText', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.2, fontSize: '0.95rem' }}>
                      Tendencia de Mensajes
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Últimos 12 meses
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label="Mensual" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  sx={{ fontWeight: 500, fontSize: '0.7rem', height: 20 }}
                />
              </Box>
              <Box sx={{ height: 'calc(100% - 70px)' }}>
                <MonthlyChart chats={chats} />
              </Box>
            </Paper>
          </Fade>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Fade in timeout={1200}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                height: 400,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: 'linear-gradient(90deg, #dc004e 0%, #ff5983 100%)',
                },
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      p: 0.8,
                      borderRadius: 1.5,
                      backgroundColor: 'secondary.light',
                      mr: 1.5,
                    }}
                  >
                    <AssessmentIcon sx={{ color: 'secondary.contrastText', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.2, fontSize: '0.95rem' }}>
                      Actividad Diaria
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Últimos 15 días
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label="Diario" 
                  size="small" 
                  color="secondary" 
                  variant="outlined"
                  sx={{ fontWeight: 500, fontSize: '0.7rem', height: 20 }}
                />
              </Box>
              <Box sx={{ height: 'calc(100% - 70px)' }}>
                <DailyChart chats={chats} />
              </Box>
            </Paper>
          </Fade>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Fade in timeout={1400}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                height: 400,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: 'linear-gradient(90deg, #2e7d32 0%, #4caf50 100%)',
                },
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      p: 0.8,
                      borderRadius: 1.5,
                      backgroundColor: 'success.light',
                      mr: 1.5,
                    }}
                  >
                    <ChatIcon sx={{ color: 'success.contrastText', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.2, fontSize: '0.95rem' }}>
                      Nuevos Chats
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Por mes
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label="Conversaciones" 
                  size="small" 
                  color="success" 
                  variant="outlined"
                  sx={{ fontWeight: 500, fontSize: '0.7rem', height: 20 }}
                />
              </Box>
              <Box sx={{ height: 'calc(100% - 70px)' }}>
                <MonthlyChatsChart chats={chats} />
              </Box>
            </Paper>
          </Fade>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Fade in timeout={1600}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                height: 400,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: 'linear-gradient(90deg, #0288d1 0%, #03dac6 100%)',
                },
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      p: 0.8,
                      borderRadius: 1.5,
                      backgroundColor: 'info.light',
                      mr: 1.5,
                    }}
                  >
                    <TrendingUpIcon sx={{ color: 'info.contrastText', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.2, fontSize: '0.95rem' }}>
                      Tendencia de Chats
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Últimos 15 días
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label="Crecimiento" 
                  size="small" 
                  color="info" 
                  variant="outlined"
                  sx={{ fontWeight: 500, fontSize: '0.7rem', height: 20 }}
                />
              </Box>
              <Box sx={{ height: 'calc(100% - 70px)' }}>
                <DailyChatsChart chats={chats} />
              </Box>
            </Paper>
          </Fade>
        </Grid>
      </Grid>

      {/* Sección de contactos y chats en vivo */}
      <Box sx={{ display: 'flex', gap: 3, width: '100%', mb: 4 }}>
        <Box sx={{ flex: 1, width: '50%' }}>
          <Fade in timeout={1800}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                height: 640,
                width: '100%',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: 'linear-gradient(90deg, #ed6c02 0%, #ff9800 100%)',
                },
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: 'warning.light',
                      mr: 2,
                    }}
                  >
                    <PeopleIcon sx={{ color: 'warning.contrastText', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Directorio de Contactos
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {chats.length} contactos activos
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label={`${chats.length}`}
                  size="small" 
                  color="warning" 
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Box sx={{ height: 'calc(100% - 80px)', width: '100%' }}>
                <ContactsList chats={chats} />
              </Box>
            </Paper>
          </Fade>
        </Box>

        <Box sx={{ flex: 1, width: '50%' }}>
          <Fade in timeout={2000}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                height: 640,
                width: '100%',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                border: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: 'linear-gradient(90deg, #d32f2f 0%, #f44336 100%)',
                },
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box display="flex" alignItems="center">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: 'error.main',
                      mr: 2,
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': {
                          transform: 'scale(0.95)',
                          boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)',
                        },
                        '70%': {
                          transform: 'scale(1)',
                          boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)',
                        },
                        '100%': {
                          transform: 'scale(0.95)',
                          boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)',
                        },
                      },
                    }}
                  />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Conversaciones en Vivo
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Últimas 5 conversaciones activas
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip 
                    label="En línea" 
                    size="small" 
                    color="success" 
                    sx={{ fontWeight: 500 }}
                  />
                  <Chip 
                    label={`${chats.slice(0, 5).length} activos`}
                    size="small" 
                    color="error" 
                    variant="outlined"
                    sx={{ fontWeight: 500 }}
                  />
                </Box>
              </Box>
              <Box sx={{ height: 'calc(100% - 80px)', width: '100%' }}>
                <RealtimeChats chats={chats.slice(0, 5)} />
              </Box>
            </Paper>
          </Fade>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default Dashboard;
